import { useEffect, useCallback, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import translations from "@/i18n";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import Home from "@/pages/Home";

const ServicePage = lazy(() => import("@/pages/ServicePage"));
const CityPage = lazy(() => import("@/pages/CityPage"));
const ExpatPage = lazy(() => import("@/pages/ExpatPage"));
const Journal = lazy(() => import("@/pages/Journal"));
const ArticlePage = lazy(() => import("@/pages/ArticlePage"));
const ServiceCityPage = lazy(() => import("@/pages/ServiceCityPage"));
const KeywordPage = lazy(() => import("@/pages/KeywordPage"));
const LegalPage = lazy(() => import("@/pages/LegalPage"));
import { keywordPages } from "@/data/keywordPages";
import { StickyCta } from "@/components/StickyCta";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { switchLangPath } from "@/utils/paths";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const Shell = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const lang = location.pathname === "/en" || location.pathname.startsWith("/en/") ? "en" : "de";
  const t = translations[lang];

  useEffect(() => {
    document.documentElement.lang = lang === "de" ? "de-CH" : "en";
  }, [lang]);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.2, smoothWheel: true });
    window.__lenis = lenis;
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  const scrollTo = useCallback((hash) => {
    const el = document.querySelector(hash);
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -72, duration: 1.4 });
    else el.scrollIntoView({ behavior: "smooth" });
  }, []);

  const setLang = useCallback(
    (newLang) => {
      if (newLang === lang) return;
      navigate(switchLangPath(location.pathname, newLang));
    },
    [lang, location.pathname, navigate]
  );

  return (
    <>
      <Navbar t={t} lang={lang} setLang={setLang} scrollTo={scrollTo} />
      <main>
        <Suspense fallback={<div className="min-h-screen bg-cream" />}>
        <Routes>
          <Route path="/" element={<Home t={t} scrollTo={scrollTo} lang={lang} />} />
          <Route path="/en" element={<Home t={t} scrollTo={scrollTo} lang={lang} />} />
          <Route path="/leistungen/:slug" element={<ServicePage t={t} lang={lang} />} />
          <Route path="/en/leistungen/:slug" element={<ServicePage t={t} lang={lang} />} />
          <Route path="/leistungen/:serviceSlug/:citySlug" element={<ServiceCityPage t={t} lang={lang} />} />
          <Route path="/en/leistungen/:serviceSlug/:citySlug" element={<ServiceCityPage t={t} lang={lang} />} />
          <Route path="/family-office-concierge-schweiz" element={<Navigate to="/private-family-office-schweiz" replace />} />
          <Route path="/en/family-office-concierge-switzerland" element={<Navigate to="/en/private-family-office-switzerland" replace />} />
          <Route path="/standorte/:slug" element={<CityPage t={t} lang={lang} />} />
          <Route path="/en/standorte/:slug" element={<CityPage t={t} lang={lang} />} />
          <Route path="/umzug-in-die-schweiz" element={<ExpatPage t={t} lang={lang} />} />
          <Route path="/en/moving-to-switzerland" element={<ExpatPage t={t} lang={lang} />} />
          <Route path="/ratgeber" element={<Journal t={t} lang={lang} />} />
          <Route path="/en/journal" element={<Journal t={t} lang={lang} />} />
          <Route path="/ratgeber/:slug" element={<ArticlePage t={t} lang={lang} />} />
          <Route path="/en/journal/:slug" element={<ArticlePage t={t} lang={lang} />} />
          <Route path="/impressum" element={<LegalPage lang={lang} type="imprint" />} />
          <Route path="/en/imprint" element={<LegalPage lang={lang} type="imprint" />} />
          <Route path="/datenschutz" element={<LegalPage lang={lang} type="privacy" />} />
          <Route path="/en/privacy" element={<LegalPage lang={lang} type="privacy" />} />
          {keywordPages.map((p) => (
            <Route key={p.slugDe} path={`/${p.slugDe}`} element={<KeywordPage t={t} lang={lang} slug={p.slugDe} />} />
          ))}
          {keywordPages.map((p) => (
            <Route key={p.slugEn} path={`/en/${p.slugEn}`} element={<KeywordPage t={t} lang={lang} slug={p.slugEn} />} />
          ))}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </main>
      <Footer t={t} scrollTo={scrollTo} lang={lang} />
      <StickyCta lang={lang} />
      <WhatsAppButton lang={lang} />
    </>
  );
};

function App() {
  return (
    <div className="min-h-screen bg-cream text-ink font-body overflow-x-clip">
      <Toaster position="top-center" richColors />
      <BrowserRouter>
        <ScrollToTop />
        <Shell />
      </BrowserRouter>
    </div>
  );
}

export default App;
